import { ValidationStatuses } from '@sunwebgroup/ui-components/uds'
import React from 'react'

// eslint-disable-next-line regexp/no-obscure-range
export const namePattern = /^[A-ZÀ-ÿ][A-ZÀ-ÿ' -]{1,49}$/i
export const datePattern = /^[1-9]\d{3}-\d{2}-\d{2}$/ // YYYY-MM-DD format
export const emailPattern = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i

export const isValidDateFormat = (val: string) => datePattern.test(val)

export function validateAgeBetweenDates(value: string, min: Date, max: Date) {
  if (!isValidDateFormat(value)) {
    return true
  }
  const date = new Date(value)
  return date >= min && date <= max
}

/**
 * Custom hook to determine the validation status of a form field based on its value, error state,
 * interaction state, and other validation criteria.
 *
 * @param {object} params - The parameters for the hook.
 * @param {string} params.value - The current value of the field.
 * @param {object} [params.error] - An optional error object containing a message if the field is invalid.
 * @param {string} [params.error.message] - The error message associated with the field.
 * @param {boolean} params.isDirty - Indicates whether the field has been interacted with.
 * @param {number} params.fieldLengthValidation - The minimum length required for the field value to be considered valid.
 * @param {boolean} params.isSubmitting - Indicates whether the form is currently being submitted.
 * @param {boolean} [params.matchPattern] - Optional flag to indicate if the field value matches a specific pattern.
 *
 * @returns {ValidationStatuses} - The current validation status of the field. Possible values are:
 * - `'default'`: The field has not been interacted with or does not meet validation criteria.
 * - `'error'`: The field has an error after submission.
 * - `'success'`: The field meets all validation criteria.
 *
 * @remarks
 * This hook tracks the interaction state of the field (e.g., whether it has been blurred or submitted)
 * and updates the validation status accordingly. It also resets the submission state if the field becomes valid.
 * Possible changes of state: default -> success, default -> submitted -> error, default -> submitted -> error (onChange restart stateMachine).
 *
 * @example
 * const validationStatus = useSmartFieldStatus({
 *   value: inputValue,
 *   error: fieldError,
 *   isDirty: isFieldDirty,
 *   fieldLengthValidation: 5,
 *   isSubmitting: isFormSubmitting,
 *   matchPattern: true,
 * });
 */
export function useSmartFieldStatus({
  value,
  error,
  isDirty,
  fieldLengthValidation,
  isSubmitting,
  matchPattern = true,
}: {
  value: string
  error?: { message: string } | undefined
  isDirty: boolean
  fieldLengthValidation: number
  isSubmitting: boolean
  matchPattern?: boolean
}): ValidationStatuses {
  const [wasBlurred, setWasBlurred] = React.useState(false)
  const [wasSubmitted, setWasSubmitted] = React.useState(false)
  const [wasInvalid, setWasInvalid] = React.useState(false)
  const trimmedValue = value?.trim() ?? ''
  React.useEffect(() => {
    if (isDirty && !wasBlurred) {
      setWasBlurred(true)
    }
  }, [isDirty, wasBlurred])

  React.useEffect(() => {
    if (!wasSubmitted && isSubmitting) {
      setWasSubmitted(true)
    }
  }, [wasSubmitted, isSubmitting])

  React.useEffect(() => {
    if (error) {
      setWasInvalid(true)
    }
  }, [error])

  let result: ValidationStatuses = 'default'
  if (!trimmedValue && !wasSubmitted) {
    result = 'default'
  }
  else if (wasSubmitted && error) {
    result = 'error'
  }
  else if (wasBlurred && trimmedValue.length >= fieldLengthValidation && !error && matchPattern) {
    result = 'success'
  }
  else if (wasBlurred && trimmedValue.length >= fieldLengthValidation && matchPattern) {
    if (wasInvalid || wasBlurred) {
      result = 'success'
    }
  }
  return result
}
